// ALTIORA LEADS — Dashboard opérationnel complet.
// Une seule page, deux modes : « tracké » (leads persistés) et
// « détection » (résultats du dernier run pipeline). Le mode
// commence toujours par la vue « Aujourd'hui » (KPIs personnels
// + priorité #1). Zéro mock, zéro invention.
import { useState, useCallback, useEffect, useMemo } from "react";
import { RefreshCw, Upload, Filter, Search } from "lucide-react";
import {
  PageShell, PageHeader, Loader, EmptyState, GhostButton, PrimaryButton,
  NAVY, CHAMPAGNE, SLATE, GOLD, IVORY, Metric,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";
import { toast } from "sonner";

import UnifiedLeadCard from
  "@/components/altiora-leads/UnifiedLeadCard";
import EvidenceDrawer from
  "@/components/altiora-leads/EvidenceDrawer";
import TopSignalsBanner from
  "@/components/altiora-leads/TopSignalsBanner";
import CsvImportModal from
  "@/components/altiora-leads/CsvImportModal";
import LeadHistoryDrawer from
  "@/components/altiora-leads/LeadHistoryDrawer";
import TodayHero from
  "@/components/altiora-leads/TodayHero";
import TerritoryPicker, { loadPersistedTerritory } from
  "@/components/altiora-leads/TerritoryPicker";
import { STATUS_ORDER, humanStatus } from
  "@/lib/altiora-leads-i18n";

const DEFAULT_TERRITORY = {
  label: "Québec",
  provinces: ["QC"],
  cities: [],
  audience_types: ["dirigeants_pme"],
  offer_type: "b2b_services",
};

const ALL_SOURCES = ["PROCUREMENT_SEAO", "REGISTRY_ISED",
                     "NEWS_CNW", "USER_IMPORT"];

// Convertit un record persisté vers le format attendu par UnifiedLeadCard
function recordToLeadShape(rec) {
  return {
    entity_id: rec.entity_id,
    entity_name: rec.entity_name,
    lead_type: rec.lead_type,
    tier: rec.tier,
    score: rec.score,
    confidence: rec.confidence,
    why_now: rec.why_now,
    why_for_me: rec.why_for_me,
    signals: rec.signals || [],
    decision_maker: rec.decision_maker || {},
    contactability: rec.contactability || {},
    evidence: rec.evidence || [],
    recommended_action: rec.recommended_action,
    sources: rec.sources || [],
    cross_source: !!rec.cross_source,
    bn: rec.bn, jurisdiction: rec.jurisdiction,
    address: rec.address || {},
  };
}

export default function AltioraLeads() {
  const [running, setRunning] = useState(false);
  const [detection, setDetection] = useState(null);
  const [tracked, setTracked] = useState([]);
  const [today, setToday] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [records, setRecords] = useState({}); // entity_id → record
  const [transitionsSchema, setTransitionsSchema] = useState(null);

  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);

  // Filtres
  const [tierFilter, setTierFilter] = useState("all");
  const [contactFilter, setContactFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("today"); // today | detection | tracked

  // Territoire persistant
  const [territory, setTerritory] = useState(() =>
    loadPersistedTerritory() || DEFAULT_TERRITORY);

  // ─── Loaders ─────────────────────────────────────────────
  const loadAlerts = useCallback(async (force = false) => {
    setAlertsLoading(true);
    try {
      const url = "/leads-engine/alerts/top-signals?limit=5"
                    + (force ? "&force_refresh=1" : "");
      const r = await api.get(url, { timeout: 180000 });
      setAlerts(r.data?.alerts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Signaux du jour indisponibles.");
      setAlerts([]);
    } finally { setAlertsLoading(false); }
  }, []);

  const loadToday = useCallback(async () => {
    try {
      const r = await api.get("/leads-engine/today");
      setToday(r.data);
    } catch { /* silent — Today est optionnel visuellement */ }
  }, []);

  const loadTracked = useCallback(async () => {
    try {
      const r = await api.get("/leads-engine/leads?limit=200");
      setTracked(r.data?.items || []);
      const map = {};
      (r.data?.items || []).forEach((x) => { map[x.entity_id] = x; });
      setRecords(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Historique des leads indisponible.");
    }
  }, []);

  const loadSchema = useCallback(async () => {
    try {
      const r = await api.get("/leads-engine/lifecycle/schema");
      setTransitionsSchema(r.data?.transitions || null);
    } catch { /* silent — fallback géré côté composant */ }
  }, []);

  const runPipeline = useCallback(async () => {
    setRunning(true);
    try {
      const r = await api.post("/leads-engine/unified-leads", {
        territory, sources: ALL_SOURCES, limit: 20,
        max_per_source: 1500,
      }, { timeout: 300000 });
      setDetection(r.data);
      setView("detection");
      // Rafraîchit les alertes (le pipeline a modifié le dataset)
      await loadAlerts(true);
      toast.success(`${r.data.count} opportunité(s) qualifiée(s).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail
                    || "Le moteur de leads n'a pas répondu.");
    } finally { setRunning(false); }
  }, [territory, loadAlerts]);

  useEffect(() => {
    loadAlerts();
    loadToday();
    loadTracked();
    loadSchema();
  }, [loadAlerts, loadToday, loadTracked, loadSchema]);

  // ─── Callbacks lifecycle ─────────────────────────────────
  const onUpsertRecord = useCallback((rec) => {
    setRecords((prev) => ({ ...prev, [rec.entity_id]: rec }));
  }, []);

  const onLifecycleChange = useCallback((rec) => {
    setRecords((prev) => ({ ...prev, [rec.entity_id]: rec }));
    loadToday();
    loadTracked();
  }, [loadToday, loadTracked]);

  const onCrmSync = useCallback((result) => {
    if (result?.record) onLifecycleChange(result.record);
  }, [onLifecycleChange]);

  // ─── Datasets calculés selon la vue ──────────────────────
  const detectionItems = detection?.items || [];

  const sourceItems = useMemo(() => {
    if (view === "detection") {
      return detectionItems.map((l) => ({
        lead: l, record: records[l.entity_id] || null,
      }));
    }
    return tracked.map((rec) => ({
      lead: recordToLeadShape(rec), record: rec,
    }));
  }, [view, detectionItems, tracked, records]);

  const filtered = useMemo(() => {
    return sourceItems.filter(({ lead, record }) => {
      if (tierFilter !== "all" && lead.tier !== tierFilter) return false;
      const cs = lead?.contactability?.status;
      if (contactFilter === "green"
          && !["allowed_lcap_10_9", "user_managed"].includes(cs)) return false;
      if (contactFilter === "amber"
          && !["allowed_lcap_10_9_role_only", "explicit_consent_required",
                "channel_only_offline"].includes(cs)) return false;
      if (contactFilter === "red" && cs !== "off_limits") return false;
      if (statusFilter !== "all") {
        const s = record?.status || "NEW";
        if (statusFilter === "not_ignored" && s === "IGNORED") return false;
        else if (statusFilter !== "not_ignored" && s !== statusFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!(lead.entity_name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [sourceItems, tierFilter, contactFilter, statusFilter, search]);

  // Priorité #1 → CTA doit ouvrir la card correspondante
  const preparePriority = useCallback((priorityRecord) => {
    setView("tracked");
    setStatusFilter("all");
    setSearch(priorityRecord?.entity_name || "");
  }, []);

  return (
    <PageShell testid="altiora-leads-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA Leads"
          title="Sur quoi agir aujourd'hui ?"
          subtitle="Croisement multi-sources en temps réel · cycle de vie complet du lead · zéro donnée fabriquée."
          testid="altiora-leads-header"
          actions={
            <div className="flex gap-2 flex-wrap">
              <GhostButton onClick={() => (window.location.href = "/app/campaigns")}
                           testid="altiora-open-campaigns">
                Mes campagnes
              </GhostButton>
              <GhostButton onClick={() => setCsvOpen(true)}
                           testid="altiora-import-csv">
                <Upload className="h-3 w-3 inline mr-1" />
                Importer mes contacts
              </GhostButton>
              <PrimaryButton onClick={runPipeline} disabled={running}
                             testid="altiora-run-pipeline">
                <RefreshCw className="h-3 w-3 inline mr-1" />
                {running ? "Analyse en cours…"
                             : (detection ? "Nouveau croisement"
                                              : "Détecter les opportunités")}
              </PrimaryButton>
            </div>
          }
        />

        {/* Today Hero */}
        <TodayHero today={today} alerts={alerts}
                   onPreparePriority={preparePriority} />

        {/* Top Signals */}
        {alertsLoading ? (
          <div className="mt-4 text-[11px] font-mono"
               style={{ color: SLATE }}
               data-testid="alerts-loading">
            Recherche des signaux du jour…
          </div>
        ) : (
          <TopSignalsBanner alerts={alerts} />
        )}

        {/* Territory picker */}
        <TerritoryPicker value={territory} onChange={setTerritory} />

        {/* Compteurs pipeline (après détection) */}
        {detection && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3"
               data-testid="altiora-leads-counters">
            <Metric label="Entités analysées"
                    value={detection.counters?.entities ?? 0}
                    testid="metric-entities" />
            <Metric label="Multi-sources"
                    value={detection.counters?.cross_source_resolved ?? 0}
                    hint="Plusieurs sources concordent"
                    testid="metric-cross-source" />
            <Metric label="Décideurs identifiés"
                    value={detection.counters?.decision_makers_identified ?? 0}
                    testid="metric-decision-makers" />
            <Metric label="Leads contactables"
                    value={detection.counters?.contactable_leads ?? 0}
                    testid="metric-contactable" />
          </div>
        )}

        {/* Switch vue tracké/détection */}
        <div className="mt-8 flex items-center gap-2 flex-wrap"
             data-testid="view-switch">
          {[
            { k: "detection", label: `Dernière détection (${detectionItems.length})` },
            { k: "tracked",   label: `Mes leads suivis (${tracked.length})` },
          ].map((v) => (
            <button key={v.k}
                    data-testid={`view-${v.k}`}
                    onClick={() => setView(v.k)}
                    className="text-[12px] px-3 py-1.5 rounded-full transition-all"
                    style={{ background: view === v.k ? NAVY : "transparent",
                             color: view === v.k ? IVORY : NAVY,
                             border: `1px solid ${view === v.k ? NAVY : CHAMPAGNE}55` }}>
              {v.label}
            </button>
          ))}
        </div>

        {/* Recherche + filtres */}
        <div className="mt-4 flex flex-wrap items-center gap-2"
             data-testid="altiora-leads-filters">
          <div className="flex items-center gap-1 rounded-full px-3 py-1"
               style={{ border: `1px solid ${CHAMPAGNE}55` }}>
            <Search className="h-3 w-3" style={{ color: SLATE }} />
            <input data-testid="altiora-search"
                   type="text" placeholder="Rechercher une entité…"
                   value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   className="text-[12px] bg-transparent outline-none"
                   style={{ color: NAVY, minWidth: "180px" }} />
          </div>

          <Filter className="h-3 w-3 ml-2" style={{ color: SLATE }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
                style={{ color: SLATE }}>Tier</span>
          {["all", "A", "B", "C"].map((t) => (
            <button key={t}
                    data-testid={`filter-tier-${t}`}
                    onClick={() => setTierFilter(t)}
                    className="text-[11px] px-2.5 py-1 rounded-full transition-all"
                    style={{ background: tierFilter === t ? NAVY : "transparent",
                             color: tierFilter === t ? IVORY : NAVY,
                             border: `1px solid ${tierFilter === t ? NAVY : CHAMPAGNE}55` }}>
              {t === "all" ? "Tous" : t}
            </button>
          ))}

          <span className="text-[10px] font-mono uppercase tracking-[0.22em] ml-2"
                style={{ color: SLATE }}>Contact</span>
          {[
            { k: "all",   label: "Tous" },
            { k: "green", label: "🟢 Possible" },
            { k: "amber", label: "🟠 À vérifier" },
            { k: "red",   label: "🔴 Interdit" },
          ].map((f) => (
            <button key={f.k}
                    data-testid={`filter-contact-${f.k}`}
                    onClick={() => setContactFilter(f.k)}
                    className="text-[11px] px-2.5 py-1 rounded-full transition-all"
                    style={{ background: contactFilter === f.k ? NAVY : "transparent",
                             color: contactFilter === f.k ? IVORY : NAVY,
                             border: `1px solid ${contactFilter === f.k ? NAVY : CHAMPAGNE}55` }}>
              {f.label}
            </button>
          ))}

          {view === "tracked" && (
            <>
              <span className="text-[10px] font-mono uppercase tracking-[0.22em] ml-2"
                    style={{ color: SLATE }}>Statut</span>
              <button data-testid="filter-status-all"
                      onClick={() => setStatusFilter("all")}
                      className="text-[11px] px-2.5 py-1 rounded-full"
                      style={{ background: statusFilter === "all" ? NAVY : "transparent",
                               color: statusFilter === "all" ? IVORY : NAVY,
                               border: `1px solid ${statusFilter === "all" ? NAVY : CHAMPAGNE}55` }}>
                Tous
              </button>
              <button data-testid="filter-status-not_ignored"
                      onClick={() => setStatusFilter("not_ignored")}
                      className="text-[11px] px-2.5 py-1 rounded-full"
                      style={{ background: statusFilter === "not_ignored" ? NAVY : "transparent",
                               color: statusFilter === "not_ignored" ? IVORY : NAVY,
                               border: `1px solid ${statusFilter === "not_ignored" ? NAVY : CHAMPAGNE}55` }}>
                Actifs
              </button>
              {STATUS_ORDER.map((s) => (
                <button key={s}
                        data-testid={`filter-status-${s}`}
                        onClick={() => setStatusFilter(s)}
                        className="text-[11px] px-2.5 py-1 rounded-full"
                        style={{ background: statusFilter === s ? NAVY : "transparent",
                                 color: statusFilter === s ? IVORY : NAVY,
                                 border: `1px solid ${statusFilter === s ? NAVY : CHAMPAGNE}55` }}>
                  {humanStatus(s)}
                </button>
              ))}
            </>
          )}
        </div>

        {/* Résultats */}
        <div className="mt-6" data-testid="altiora-leads-list">
          {running && (
            <div className="rounded-xl p-8 bg-white text-center"
                 style={{ border: `1px solid ${CHAMPAGNE}33` }}>
              <Loader label="Croisement des sources publiques…" />
              <p className="text-[11px] mt-3" style={{ color: SLATE }}>
                Territoire : {territory.label} · Audience : {territory.audience_types?.[0]} ·
                Offre : {territory.offer_type}. Cette étape prend 30-90 s.
              </p>
            </div>
          )}

          {!running && view === "detection" && !detection && (
            <EmptyState icon={RefreshCw}
              title="Aucun croisement lancé aujourd'hui."
              description="Sélectionnez votre territoire ci-dessus puis cliquez sur « Détecter les opportunités »."
              testid="altiora-leads-idle" />
          )}

          {!running && view === "tracked" && tracked.length === 0 && (
            <EmptyState icon={Filter}
              title="Aucun lead suivi pour l'instant."
              description="Ouvrez une opportunité depuis la Dernière détection puis cliquez sur « Ajouter au CRM » ou changez son statut — elle apparaîtra ici."
              testid="altiora-leads-empty-tracked" />
          )}

          {!running && filtered.length === 0 && sourceItems.length > 0 && (
            <EmptyState icon={Filter}
              title="Aucun lead ne correspond aux filtres actuels."
              description="Élargissez les filtres ou effacez la recherche."
              testid="altiora-leads-empty-filter" />
          )}

          {!running && filtered.length > 0 && (
            <div className="grid gap-4">
              {filtered.map(({ lead, record }) => (
                <UnifiedLeadCard key={lead.entity_id + (record?.record_id || "")}
                                 lead={lead}
                                 record={record}
                                 transitionsSchema={transitionsSchema}
                                 onOpenEvidence={setSelectedEvidence}
                                 onOpenHistory={setSelectedHistory}
                                 onUpsert={onUpsertRecord}
                                 onLifecycleChange={onLifecycleChange}
                                 onCrmSync={onCrmSync} />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedEvidence && (
        <EvidenceDrawer lead={selectedEvidence}
                        onClose={() => setSelectedEvidence(null)} />
      )}
      {selectedHistory && (
        <LeadHistoryDrawer entityId={selectedHistory.entity_id}
                           entityName={selectedHistory.entity_name}
                           onClose={() => setSelectedHistory(null)} />
      )}
      <CsvImportModal open={csvOpen}
                      onClose={() => setCsvOpen(false)}
                      onCrossReferenced={(rep) => {
                        setDetection(rep);
                        setView("detection");
                        loadToday();
                      }} />
    </PageShell>
  );
}
